import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Tooltip, message } from 'antd';
import {
  AimOutlined,
  CheckSquareOutlined,
  EditOutlined,
  FileExcelOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { FieldMeta } from '../api/types';
import { useAppDispatch, useAppState } from '../store/hooks';
import { track } from '../utils/track';
import { recommendTemplates, type Recommendation } from '../templates/recommend';
import { fetchAiQuota } from '../ai/api';
import { getBaseUserId } from '../api/bitable';
import { useContractTemplates } from '../contract/useContractTemplates';
import { genId } from '../contract/types';
import type { ContractTemplate } from '../contract/types';
import { useTemplates } from '../store/useTemplates';
import { getCheckedRecordIds, getSelectedRecordIds } from '../api/bitable';
import { ContextStrip } from '../components/shell/ContextStrip';
import { NumberedSection } from '../components/shell/NumberedSection';
import { ActiveTemplateChip, type TemplateLike } from '../components/shell/ActiveTemplateChip';
import { AiGenerateModal } from '../components/AiGenerateModal';
import { ExcelImportModal } from '../components/ExcelImportModal';
import { applyLibraryTemplate, type LibraryTemplate } from '../templates/library';
import { RangePanel } from '../components/RangePanel';
import { PreviewPane } from '../components/PreviewPane';
import { ContractPreview } from '../components/ContractMode/ContractPreview';
import { summarizeContractTemplate, summarizeTableTemplate } from './util';
import { launchFullscreen } from '../fullscreen/launch';
import { TemplateThumb } from '../components/shell/TemplateThumb';
import { MyTemplatesStrip } from '../components/shell/MyTemplatesStrip';
import { buildDemoSections } from '../templates/demoCanvas';

interface Props {
  fieldMetas: FieldMeta[];
  tableName: string | null;
  bootstrapError: string | null;
  /** Reserved for future loading indicator inside Home (e.g., skeleton). */
  bootstrapLoading?: boolean;
  visibleFieldIds: string[];
  onRefresh: () => void;
}

function CurrentRecordChip({
  recordId,
  onUseChecked,
  pickingChecked,
}: {
  recordId: string | null;
  onUseChecked: () => void;
  pickingChecked: boolean;
}) {
  return (
    <div
      style={{
        margin: '10px 16px 0',
        padding: '8px 12px',
        background: recordId ? 'var(--accent-soft)' : 'var(--surface-2)',
        border: '1px solid ' + (recordId ? 'transparent' : 'var(--border-soft)'),
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11.5,
        color: recordId ? 'var(--accent)' : 'var(--ink-3)',
        flexWrap: 'wrap',
      }}
    >
      <AimOutlined style={{ fontSize: 12 }} />
      {recordId ? (
        <>
          <span style={{ fontWeight: 600 }}>已选记录</span>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--ink-2)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {recordId}
          </span>
        </>
      ) : (
        <>
          <span style={{ fontWeight: 500 }}>未选中记录</span>
          <span style={{ flex: 1 }} />
        </>
      )}
      <Tooltip
        title={
          <span style={{ fontSize: 11.5 }}>
            插件每 0.8 秒自动同步表格里的勾选状态。
            <br />
            如果想立即跳过等待，点这里强制同步一次。
          </span>
        }
      >
        <Button
          size="small"
          icon={<CheckSquareOutlined />}
          onClick={onUseChecked}
          loading={pickingChecked}
        >
          立即同步
        </Button>
      </Tooltip>
    </div>
  );
}

/** 首次进入（当前模式下还没有任何模板）的任务式引导：
 *  两张卡各对应一种玩法，点按钮直接建模板进编辑器 —— 不让用户猜。 */
/**
 * 「为你推荐」——按当前表的字段挑出最像的几张内置单据，点一下直接出成品。
 *
 * 首屏是流失最狠的一环：326 个打开过插件的用户里 272 个从没打印过。
 * 让他们面对一张白纸自己想「该排什么」太难了，直接给几张已经对上字段的
 * 单据，点「使用」就能看到自己表里的真数据。
 */
function RecommendCards({
  recos,
  onUse,
  fieldMetas,
  tableId,
}: {
  recos: Recommendation[];
  onUse: (lib: LibraryTemplate) => void;
  fieldMetas: FieldMeta[];
  tableId: string;
}) {
  if (recos.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--ink)' }}>为你推荐</div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: -4 }}>
        根据表格字段，从采购销售 / 人事行政 / 财务等内置单据中智能匹配
      </div>
      {/* 横向平铺：侧栏够宽就两张并排，窄了自动折成一列 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {recos.map(({ lib, matched, strict }) => (
          <div key={lib.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div
              style={{
                border: '1px solid var(--border-soft)',
                background: 'var(--surface-1)',
                borderRadius: 10,
                overflow: 'hidden',
              }}
            >
              <TemplateThumb template={applyLibraryTemplate(lib, tableId || '__no_table__')} fieldMetas={fieldMetas} height={150} />
              <div style={{ padding: '8px 12px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 13.5, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {lib.name}
                </div>
                <Button type="primary" size="small" onClick={() => onUse(lib)}>
                  使用
                </Button>
              </div>
            </div>
            <div
              style={{ fontSize: 11.5, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 2px' }}
              title={matched.join('、')}
            >
              {matched.length > 0
                ? `匹配字段：${matched.slice(0, 3).join('、')}${matched.length > 3 ? ` 等 ${matched.length} 个字段` : ''}`
                : '字段按名称自动匹配，用后可在画布里改绑'}
              {!strict && matched.length > 0 ? '（部分匹配）' : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OnboardingCards({
  recos,
  aiFree,
  onUseReco,
  onOpenGallery,
  onCreateRecord,
  onCreateDemo,
  onAiGenerate,
  onImportExcel,
  fieldMetas,
  tableId,
}: {
  recos: Recommendation[];
  aiFree: number;
  onUseReco: (lib: LibraryTemplate) => void;
  onOpenGallery: () => void;
  onCreateRecord: () => void;
  onCreateDemo: () => void;
  onAiGenerate: () => void;
  onImportExcel: () => void;
  fieldMetas: FieldMeta[];
  tableId: string;
}) {

  /** 竞品那种一行一个入口的列表：图标 + 标题 + 一句话 + 右箭头。四张大卡摞在一起太重。 */
  const row = (icon: React.ReactNode, title: string, desc: string, onClick: () => void, primary?: boolean) => (
    <button
      className="pp-bare-button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 14px',
        border: primary ? '1px solid var(--accent)' : '1px solid var(--border-soft)',
        background: 'var(--surface-1)',
        borderRadius: 10,
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: primary ? 'var(--accent)' : 'var(--surface-2)',
          color: primary ? 'var(--accent-ink)' : 'var(--ink-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, fontSize: 13.5, color: 'var(--ink)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.5 }}>{desc}</span>
      </span>
      <span style={{ color: 'var(--ink-4)', fontSize: 12, flexShrink: 0 }}>›</span>
    </button>
  );

  return (
    <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <RecommendCards recos={recos} onUse={onUseReco} fieldMetas={fieldMetas} tableId={tableId} />

      <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--ink)', marginTop: 12 }}>从零开始</div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: -6, marginBottom: 2 }}>
        逛模板商城选用现成单据，或用 Demo 画布练手
      </div>
      {row(<span style={{ fontSize: 16 }}>🏬</span>, '模板商城', '按分类浏览内置单据，一键选用并自动绑定字段', onOpenGallery)}
      {row(<span style={{ fontSize: 18, lineHeight: 1 }}>+</span>, '新建 Demo 画布（新手教程）', '带交互示例与画布引导，快速上手插块与字段绑定', onCreateDemo)}
      {row(
        <ThunderboltOutlined />,
        aiFree > 0 ? `AI 生成排版 · 本月免费 ${aiFree} 次` : 'AI 生成排版',
        'AI 读当前表的字段自动排版，也可以上传一张单据照片让它照着仿',
        onAiGenerate,
      )}
      {row(
        <FileExcelOutlined style={{ color: '#1d6f42' }} />,
        '导入 Excel 单据',
        '手里有现成的报价单 / 出库单 .xlsx？合并格、列宽原样还原，并按字段名自动绑定',
        onImportExcel,
      )}
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>
        想完全自己搭？
        <a onClick={onCreateRecord} style={{ color: 'var(--accent)', marginLeft: 4, cursor: 'pointer' }}>
          从空白模板开始 →
        </a>
      </div>
    </div>
  );
}

export function HomePage({
  fieldMetas,
  tableName,
  bootstrapError,
  onRefresh,
}: Props) {
  const state = useAppState();
  const dispatch = useAppDispatch();
  const contractTpl = useContractTemplates(state.context?.tableId ?? null);
  const tableTpl = useTemplates(state.context?.tableId ?? null);

  const isContract = state.mode === 'contract';
  // 「视图模板」是旧概念，新版表格的「本表多条记录」已经覆盖它。没有存量视图模板
  // 的用户不该再看到两种模板 —— 停在 table 模式的拉回来。
  useEffect(() => {
    if (!isContract && tableTpl.templates.length === 0) dispatch({ type: 'SET_MODE', mode: 'contract' });
  }, [isContract, tableTpl.templates.length, dispatch]);

  const currentContractTemplate = useMemo(
    () =>
      contractTpl.selectedId
        ? contractTpl.templates.find((t) => t.id === contractTpl.selectedId) ?? null
        : null,
    [contractTpl.selectedId, contractTpl.templates],
  );

  const currentTableTemplate = useMemo(
    () =>
      tableTpl.selectedId
        ? tableTpl.templates.find((t) => t.id === tableTpl.selectedId) ?? null
        : null,
    [tableTpl.selectedId, tableTpl.templates],
  );

  const chipCurrent: TemplateLike | null = isContract
    ? currentContractTemplate
      ? {
          id: currentContractTemplate.id,
          name: currentContractTemplate.name,
          summary: summarizeContractTemplate(currentContractTemplate),
        }
      : null
    : currentTableTemplate
      ? {
          id: currentTableTemplate.id,
          name: currentTableTemplate.name,
          summary: summarizeTableTemplate(currentTableTemplate),
        }
      : null;

  const chipList: TemplateLike[] = isContract
    ? contractTpl.templates.map((t) => ({
        id: t.id,
        name: t.name,
        summary: summarizeContractTemplate(t),
      }))
    : tableTpl.templates.map((t) => ({
        id: t.id,
        name: t.name,
        summary: summarizeTableTemplate(t),
      }));

  const onSelectTemplate = (id: string) => {
    if (isContract) {
      contractTpl.setSelected(id);
      return;
    }
    tableTpl.setSelected(id);
    // 视图模板存的是「印哪些字段、什么排版」。以前只记了一下选中项，
    // 从不把内容应用到画布上 —— 客户换模板发现预览纹丝不动。
    const tpl = tableTpl.templates.find((t) => t.id === id);
    if (tpl) {
      dispatch({ type: 'APPLY_TEMPLATE', fieldIds: tpl.fieldIds, settings: tpl.settings });
    }
  };

  const onEditTemplate = (id: string) => {
    dispatch({ type: 'NAV', view: 'editor', editingTemplateId: id });
  };

  /** 上下文没就绪就新建，模板会落进 __no_table__ 桶里，进编辑器直接「模板已不存在」。 */
  const ensureContext = (): boolean => {
    if (state.context) return true;
    message.warning('正在连接数据表，请稍候再新建模板');
    return false;
  };

  const onCreateNew = () => {
    if (!ensureContext()) return;
    track('tpl_create');
    if (isContract) {
      const t = contractTpl.create('未命名模板');
      if (t) dispatch({ type: 'NAV', view: 'editor', editingTemplateId: t.id });
    } else {
      const t = tableTpl.create(
        '未命名模板',
        state.fieldOptions.filter((f) => f.checked).map((f) => f.id),
        state.settings,
      );
      if (t) dispatch({ type: 'NAV', view: 'editor', editingTemplateId: t.id });
    }
  };

  const onOpenLibrary = () => dispatch({ type: 'NAV', view: 'templates' });

  // ── 首开引导：两种模式都还没有任何模板 = 用户第一次用 ──
  const isFirstRun =
    contractTpl.templates.length === 0 && tableTpl.templates.length === 0;

  // 按当前表的字段推荐内置单据（命中率不够就不推，宁可不推也别推一片空白）
  // 只放两张：一行并排正好，第三张会单独掉到第二行，首屏被推荐卡占满
  const recos = useMemo(() => recommendTemplates(fieldMetas, 2), [fieldMetas]);

  // AI 本月还剩几次免费 —— 首屏直接标出来，用户才知道点了不用掏钱
  const [aiFree, setAiFree] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const id = await getBaseUserId();
        const q = await fetchAiQuota(id);
        if (!cancelled) setAiFree(q.trialRemaining);
      } catch {
        /* 取不到就不显示次数，不影响功能 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * 新建完直接进大画布，不再先落到侧边栏那个窄编辑器 —— 那是回头微调用的。
   * 标签页被拦时才退回旧路径。
   */
  const openCanvasFor = (t: ContractTemplate) => {
    void launchFullscreen({
      template: t,
      tableId: state.context?.tableId ?? '',
      recordId: state.contractRecordId,
      fieldMetas,
    }).then((r) => {
      if (r !== 'opened') dispatch({ type: 'NAV', view: 'editor', editingTemplateId: t.id });
    });
  };

  const createRecordTemplate = () => {
    if (!ensureContext()) return;
    track('tpl_create');
    if (!isContract) dispatch({ type: 'SET_MODE', mode: 'contract' });
    const t = contractTpl.create('未命名模板');
    if (t) openCanvasFor(t);
  };

  /** Demo 画布：用这张表的真实字段拼好一份示例，进画布边看边改。 */
  const createDemoTemplate = () => {
    if (!ensureContext()) return;
    track('tpl_create');
    if (!isContract) dispatch({ type: 'SET_MODE', mode: 'contract' });
    // 一次 insert 完整模板。create 完紧接着 update 会用上一轮的 templates 闭包，
    // 把刚建的那个覆盖掉 —— 全屏页打开就是「找不到这个模板」。
    const now = Date.now();
    const t = contractTpl.insert({
      id: genId(),
      tableId: state.context?.tableId ?? '__no_table__',
      name: `${tableName || '示例'} · Demo`,
      sections: buildDemoSections(fieldMetas, tableName || ''),
      settings: { paperSize: 'A4', orientation: 'portrait', marginMm: 15, baseFontSizePx: 12 },
      createdAt: now,
      updatedAt: now,
    });
    if (t) openCanvasFor(t);
  };

  // ── AI 生成排版：产出的是一份完整 ContractTemplate，落库路径与模板库一致 ──
  const [aiOpen, setAiOpen] = useState(false);
  const applyAiTemplate = (tpl: ContractTemplate) => {
    track('tpl_create');
    track('ai_done');
    if (!isContract) dispatch({ type: 'SET_MODE', mode: 'contract' });
    const t = contractTpl.insert({
      ...tpl,
      id: genId(),
      tableId: state.context?.tableId ?? '__no_table__',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    if (t) openCanvasFor(t);
  };

  // ── 导入 Excel：读文件在弹窗里做，用户点「打开画布」时才落库（那一下是真点击，新标签页不会被拦）──
  const excelInputRef = useRef<HTMLInputElement | null>(null);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const pickExcel = () => {
    if (!ensureContext()) return;
    excelInputRef.current?.click();
  };
  const applyExcelTemplate = (body: Pick<ContractTemplate, 'name' | 'sections' | 'settings'>) => {
    track('tpl_create');
    track('excel_import');
    if (!isContract) dispatch({ type: 'SET_MODE', mode: 'contract' });
    const now = Date.now();
    const t = contractTpl.insert({
      ...body,
      id: genId(),
      tableId: state.context?.tableId ?? '__no_table__',
      createdAt: now,
      updatedAt: now,
    });
    setExcelFile(null);
    if (t) openCanvasFor(t);
  };

  // ── 模板库：套用即出成品（记录模板模式）──
  const pickLibrary = (lib: LibraryTemplate) => {
    if (!ensureContext()) return;
    track('tpl_create');
    if (!isContract) dispatch({ type: 'SET_MODE', mode: 'contract' });
    const t = contractTpl.insert(applyLibraryTemplate(lib, state.context?.tableId ?? '__no_table__'));
    if (t) openCanvasFor(t);
  };

  // ── Auto-sync row-checkbox selection ────────────────────────────────
  // The Lark Base SDK only fires onSelectionChange for the cell cursor;
  // checking/unchecking row checkboxes is silent. Poll while we're on
  // home + the tab is visible so the preview tracks the checkbox.
  //
  // 勾选列表是按视图顺序返回的，不是按勾选先后。以前取 ids[0]，第 1 行勾着
  // 再勾第 2 行，列表变成 [1,2] 而第一条没变，预览就不动 —— 用户看到的就是
  // "打钩不同的行，明细表不跟着换"。改成和上一轮比对：新勾上的那行优先；
  // 只有取消勾选且当前记录被取消了，才退到剩下的第一条。
  const lastCheckedRef = useRef<Set<string>>(new Set());
  const currentRecordRef = useRef<string | null>(null);
  currentRecordRef.current = state.contractRecordId;
  useEffect(() => {
    if (!isContract) return;
    if (!state.context) return;

    const ctx = state.context;
    let cancelled = false;
    // 换表了，上一张表的勾选基准作废
    lastCheckedRef.current = new Set();

    const poll = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible')
        return;
      const ids = await getCheckedRecordIds(ctx.tableId, ctx.viewId);
      if (cancelled || ids === null) return; // 取不到 ≠ 没勾选，跳过这一轮
      const prev = lastCheckedRef.current;
      const next = new Set(ids);
      const added = ids.filter((id) => !prev.has(id));
      const removedCurrent =
        currentRecordRef.current !== null &&
        prev.has(currentRecordRef.current) &&
        !next.has(currentRecordRef.current);
      lastCheckedRef.current = next;
      // 全部取消勾选（['rec_5'] → []）时不动预览：保留光标/上一次选中的记录，
      // 用户最后一次明确的动作说了算。
      let target: string | null = null;
      if (added.length > 0) target = added[0];
      else if (removedCurrent && ids.length > 0) target = ids[0];
      if (target && target !== currentRecordRef.current) {
        dispatch({ type: 'SET_CONTRACT_RECORD_ID', recordId: target });
      }
    };

    void poll();
    const id = window.setInterval(() => {
      void poll();
    }, 800);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isContract, state.context, dispatch]);

  const [pickingChecked, setPickingChecked] = useState(false);
  const onUseChecked = useCallback(async () => {
    if (!state.context) {
      message.warning('未连接数据表');
      return;
    }
    setPickingChecked(true);
    try {
      const ids = await getSelectedRecordIds(state.context.tableId, state.context.viewId);
      if (ids.length === 0) {
        message.info('当前没有勾选/选中的记录');
        return;
      }
      const current = state.contractRecordId;
      const pick = current && ids.includes(current) ? current : ids[0];
      dispatch({ type: 'SET_CONTRACT_RECORD_ID', recordId: pick });
      if (ids.length > 1) {
        message.success(
          `已勾选 ${ids.length} 条，预览一次只渲染 1 条（最新勾上的那条优先，批量打印请用「批量」）`,
        );
      } else {
        message.success('已同步勾选记录');
      }
    } catch (err) {
      message.error(err instanceof Error ? err.message : String(err));
    } finally {
      setPickingChecked(false);
    }
  }, [state.context, state.contractRecordId, dispatch]);

  return (
    <>
      <ContextStrip
        context={state.context}
        tableName={tableName}
        mode={state.mode}
        onModeChange={(mode) => dispatch({ type: 'SET_MODE', mode })}
        onRefresh={onRefresh}
        showModeSwitch={tableTpl.templates.length > 0}
      />

      {bootstrapError && (
        <Alert
          type="error"
          showIcon
          message="加载数据表上下文失败"
          description={bootstrapError}
          style={{ margin: '12px 16px 0', borderRadius: 8 }}
        />
      )}


      <input
        ref={excelInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) setExcelFile(f);
        }}
      />
      <ExcelImportModal
        file={excelFile}
        fieldMetas={fieldMetas}
        onClose={() => setExcelFile(null)}
        onApply={applyExcelTemplate}
      />
      <AiGenerateModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        fieldMetas={fieldMetas}
        tableName={tableName}
        onApply={applyAiTemplate}
      />

      {isFirstRun ? (
        <OnboardingCards
          recos={recos}
          aiFree={aiFree}
          onUseReco={pickLibrary}
          onOpenGallery={() => dispatch({ type: 'NAV', view: 'store' })}
          onCreateRecord={createRecordTemplate}
          onCreateDemo={createDemoTemplate}
          fieldMetas={fieldMetas}
          tableId={state.context?.tableId ?? ''}
          onAiGenerate={() => setAiOpen(true)}
          onImportExcel={pickExcel}
        />
      ) : (
        <>
      <div style={{ padding: '14px 16px 0' }}>
        {isContract && contractTpl.templates.length > 1 && (
          <MyTemplatesStrip
            templates={contractTpl.templates}
            selectedId={contractTpl.selectedId}
            fieldMetas={fieldMetas}
            onSelect={onSelectTemplate}
          />
        )}
        <ActiveTemplateChip
          mode={state.mode}
          current={chipCurrent}
          templates={chipList}
          onSelect={onSelectTemplate}
          onEdit={onEditTemplate}
          onCreateNew={onCreateNew}
          onOpenLibrary={onOpenLibrary}
        />
        {/* 原来这是个 type="link" 的裸文字，浮在模板卡外面、和上面的卡没有从属关系，
            看着像遗留元素。改成贴着卡片下沿的次要按钮，归属明确。 */}
        {isContract && (
          <Button
            size="small"
            icon={<span style={{ fontSize: 12 }}>✨</span>}
            style={{
              marginTop: 8,
              width: '100%',
              height: 30,
              fontSize: 12,
              color: 'var(--accent)',
              borderColor: 'var(--border)',
              background: 'var(--accent-soft)',
            }}
            onClick={() => dispatch({ type: 'NAV', view: 'store' })}
          >
            从模板库新建
          </Button>
        )}
        {isContract && (
          <Button
            size="small"
            icon={<FileExcelOutlined />}
            style={{ marginTop: 6, width: '100%', height: 30, fontSize: 12, borderColor: 'var(--border)' }}
            onClick={pickExcel}
          >
            导入 Excel 单据
          </Button>
        )}
      </div>

      {isContract ? (
        <>
          <CurrentRecordChip
            recordId={state.contractRecordId}
            onUseChecked={() => void onUseChecked()}
            pickingChecked={pickingChecked}
          />

          <NumberedSection num="01" label="预览 · 打印" defaultOpen>
            {currentContractTemplate && currentContractTemplate.sections.length === 0 ? (
              <div
                style={{
                  border: '1px dashed var(--border-soft)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ flex: 1, minWidth: 160 }}>
                  模板「{currentContractTemplate.name}」还没有内容区块，去编辑器添加后即可打印。
                </span>
                <Button
                  size="small"
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => onEditTemplate(currentContractTemplate.id)}
                >
                  去编辑
                </Button>
              </div>
            ) : (
              <ContractPreview
                context={state.context}
                recordId={state.contractRecordId}
                template={currentContractTemplate}
                fieldMetas={fieldMetas}
              />
            )}
          </NumberedSection>
        </>
      ) : (
        <>
          <NumberedSection num="01" label="记录范围 · 排序" defaultOpen>
            <RangePanel />
          </NumberedSection>

          <NumberedSection num="02" label="预览 · 打印" defaultOpen>
            <PreviewPane fieldMetas={fieldMetas} />
          </NumberedSection>
        </>
      )}
        </>
      )}
    </>
  );
}
